import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { analyticsApi } from '../services/analytics-api.service';

const REFRESH_STATUS_KEY = ['analytics', 'refresh-status'] as const;
const IDLE_POLL_MS = 60 * 60 * 1000;
const ACTIVE_POLL_MS = 5 * 1000;

export const useAnalyticsRefresh = () => {
  const queryClient = useQueryClient();
  const prevOldestRef = useRef<string | null>(null);

  const statusQuery = useQuery({
    queryKey: REFRESH_STATUS_KEY,
    queryFn: analyticsApi.getRefreshStatus,
    staleTime: ACTIVE_POLL_MS,
    refetchInterval: (query) => {
      const isActive = query.state.data?.isRefreshing ?? false;
      return isActive ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    },
  });

  const isRefreshing = statusQuery.data?.isRefreshing ?? false;
  const cooldownRemainingMs = statusQuery.data?.cooldownRemainingMs ?? 0;
  const canRefresh = !isRefreshing && cooldownRemainingMs <= 0;
  const currentOldest = statusQuery.data?.oldestRefresh ?? null;

  useEffect(() => {
    if (
      prevOldestRef.current !== null &&
      currentOldest !== null &&
      currentOldest !== prevOldestRef.current
    ) {
      queryClient.invalidateQueries({
        queryKey: ['analytics'],
        predicate: (query) =>
          !query.queryKey.includes('refresh-status') &&
          !query.queryKey.includes('partner-companies'),
      });
    }
    prevOldestRef.current = currentOldest;
  }, [currentOldest, queryClient]);

  const refreshMutation = useMutation({
    mutationFn: analyticsApi.triggerRefresh,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REFRESH_STATUS_KEY });
    },
  });

  const triggerRefresh = useCallback(() => {
    if (!canRefresh) return;
    refreshMutation.mutate();
  }, [canRefresh, refreshMutation]);

  return {
    status: statusQuery.data,
    isLoading: statusQuery.isLoading,
    isStale: statusQuery.data?.isStale ?? false,
    oldestRefresh: currentOldest,
    isRefreshing,
    cooldownRemainingMs,
    canRefresh,
    triggerRefresh,
    refreshError: refreshMutation.error,
  };
};
